import React, { useState, useEffect, createContext } from 'react';
import { Outlet } from 'react-router-dom';
import { isAuthenticatedViaCookie, getUserInfoFromLocalStorage } from '../../utils/utils';
import Sidebar from './Sidebar';
import MobileMenuButton from './MobileMenuButton';
import './LinkPayment.css';

/**
 * True when a page is rendered inside {@link PortalLayout}. Pages read this to skip
 * rendering their own Sidebar + mobile header (the layout provides them once). When a
 * page is used OUTSIDE this layout (e.g. the ovum/default route groups), this stays
 * false and the page renders its own shell exactly as before — so those portals are
 * unaffected.
 */
export const InPortalLayoutContext = createContext(false);

/**
 * Persistent shell for the St. Luke patient portal. Rendering the Sidebar + mobile
 * header here (once, above an <Outlet/>) means route changes only swap the page
 * content — the navbar stays mounted, so navigation is seamless (no remount/flash).
 */
const PortalLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  useEffect(() => {
    if (isAuthenticatedViaCookie()) {
      setUserInfo(getUserInfoFromLocalStorage());
    }
  }, []);

  return (
    <InPortalLayoutContext.Provider value={true}>
      <div className="flex flex-col lg:flex-row min-h-screen">
        <Sidebar
          userInfo={userInfo}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="grow min-w-0">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
              <div className="flex items-center">
                <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
              </div>
              <MobileMenuButton isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen((o) => !o)} />
            </div>
          </div>

          <Outlet />
        </div>
      </div>
    </InPortalLayoutContext.Provider>
  );
};

export default PortalLayout;
